import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Browser } from '@syncfusion/ej2-base';

import { setShowTypeNotifications } from 'redux/actions/notificationActions';
import useApi from 'hooks/useApi';

import { ReactComponent as NotificationIcon } from 'assets/icon-notification.svg';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ButtonIcon } from 'shared/components/Buttons';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';
import { colors, fonts, fontSizes, device } from 'shared/defaultTheme';
import {
  Table,
  Nav,
  NavItem,
  NavLink as NavLinkBase,
  UncontrolledTooltip,
  Spinner,
} from 'reactstrap';
import { NOTIFICATION_TYPES } from 'shared/constants/contants';
import useNotification from 'hooks/useNotification';
import { TdNotifyNameClient } from '../styles';

const NavHome = styled(Nav)`
  border-bottom: 1px solid ${({ color }) => color};
`;
const NavLink = styled(NavLinkBase)`
  border-color: ${({ border }) => border} !important;
  color: ${({ color }) => color};
  background-color: ${({ background }) => background} !important;
  font-family: ${fonts.default};
  font-weight: bold;
  cursor: pointer;
  &:hover {
    color: ${({ color }) => color};
  }
  @media ${device.sm} {
    font-size: 11px;
  }
`;

const NotificationsTable = ({
  notificationData,
  refreshNotificationsData,
  clients,
  activeTab,
  setActiveTab,
  isLoading,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { mainColor } = useSelector((state) => state.theme);
  const { isCustomer, mail } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [SelectedRemoveNotifi, setSelectedRemoveNotifi] = useState(null);
  const [showConfirmRemoveNotifica, setShowConfirmRemoveNotifica] = useState(false);
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);

  const { handleSelectNotificationReview } = useNotification();

  const [{ loading: lodingDelete }, deleteNotification] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onEdit = (currentNotification) => {
    handleSelectNotificationReview(currentNotification);
    if (activeTab === NOTIFICATION_TYPES.SINGLES) {
      dispatch(setShowTypeNotifications(false));
    } else {
      dispatch(setShowTypeNotifications(true));
    }
    const today = new Date();
    if (today >= new Date(currentNotification.date)) {
      history.push(`/notificacion_details`, { isClientNotify: false });
    } else {
      history.push(`/edit_notificacion`, { params: currentNotification });
    }
  };

  const onRemove = (currentNotification) => {
    setSelectedRemoveNotifi(currentNotification);
    setShowConfirmRemoveNotifica(true);
  };

  const onOkSuccessRemove = () => {
    setSelectedRemoveNotifi(null);
    onToggleSuccessRemove();
    refreshNotificationsData();
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveNotifica(false);
    setSelectedRemoveNotifi(null);
  };

  const removeSuccess = () => {
    try {
      if (SelectedRemoveNotifi !== undefined && SelectedRemoveNotifi !== null) {
        setShowConfirmRemoveNotifica(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onOkRemove = async () => {
    try {
      const { clientNavisionId, clientId, id } = SelectedRemoveNotifi;
      if (lodingDelete) {
        return;
      }
      const {
        data: { errors },
      } = await deleteNotification({
        url: `${
          window.API_V1
        }/cli/notifications/${clientNavisionId}/${clientId}/${encodeURIComponent(mail)}/${id}`,
      });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      setShowConfirmRemoveNotifica(false);
      onToggleErrorRemove();
    }
  };

  const getNameClient = (clientId) => {
    const findClientName = clients?.find((x) => x.clientId === clientId);

    return findClientName?.clientName;
  };

  return (
    <>
      <NavHome tabs className="home__document-table" color={mainColor}>
        <NavItem>
          <NavLink
            className="text-uppercase ml-1"
            border={mainColor}
            color={activeTab === NOTIFICATION_TYPES.SINGLES ? colors.white : mainColor}
            background={activeTab === NOTIFICATION_TYPES.SINGLES ? mainColor : colors.white}
            onClick={() => {
              setActiveTab(NOTIFICATION_TYPES.SINGLES);
            }}
          >
            {isCustomer ? t('notifications_text_individual') : t('notifications_text_personals')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className="text-uppercase"
            border={mainColor}
            color={activeTab === NOTIFICATION_TYPES.MULTIPLES ? colors.white : mainColor}
            background={activeTab === NOTIFICATION_TYPES.MULTIPLES ? mainColor : colors.white}
            onClick={() => {
              setActiveTab(NOTIFICATION_TYPES.MULTIPLES);
            }}
          >
            {isCustomer ? t('notifications_text_massive') : t('notifications_text_diffusion')}
          </NavLink>
        </NavItem>
      </NavHome>

      {isLoading ? (
        <div className="d-flex justify-content-center h-100 align-items-center">
          <Spinner color={mainColor} />
        </div>
      ) : (
        <>
          <Table className="home__notifi-table home-mobile-table">
            <thead className="home-thead">
              {isCustomer ? (
                <tr>
                  {activeTab === NOTIFICATION_TYPES.SINGLES && <th>{t('general_client')}</th>}
                  <th>{t('custom_field_date_fields')}</th>
                  <th>{t('notification_column_issue')}</th>
                  <th>{}</th>
                </tr>
              ) : (
                <tr />
              )}
            </thead>
            <Tbody>
              {notificationData.map((notification, index) => (
                <TBodyTr key={notification.id} numAnswersUnread={notification.numAnswersUnread}>
                  {isCustomer && activeTab === NOTIFICATION_TYPES.SINGLES && (
                    <TdNotifyNameClient id={`notification-client-id-${index}`}>
                      <div>
                        <p>{getNameClient(notification.clientId)}</p>
                      </div>
                      <UncontrolledTooltip
                        placement="bottom"
                        target={`notification-client-id-${index}`}
                        autohide={false}
                      >
                        {getNameClient(notification.clientId)}
                      </UncontrolledTooltip>
                    </TdNotifyNameClient>
                  )}
                  <TdDate>
                    {Browser.isDevice
                      ? moment(notification.date).format('DD/MM/YYYY')
                      : moment(notification.date).format('DD/MM/YYYY')}
                  </TdDate>
                  <td id={`notify-name-${index}`}>
                    <div
                      aria-hidden="true"
                      onClick={() => {
                        // dispatch(selectCurrentClient(undefined));
                        onEdit(notification);
                      }}
                      className="link-name-notification"
                    >
                      <p>{notification.name}</p>
                    </div>
                    <UncontrolledTooltip
                      placement="bottom"
                      target={`notify-name-${index}`}
                      autohide={false}
                    >
                      {notification.name ?? ''}
                    </UncontrolledTooltip>
                  </td>
                  <TdActions>
                    <div className="d-flex">
                      <ButtonIcon
                        id={`edit-icon-notification-${index}`}
                        title={t('clients_edit_client')}
                        type="button"
                        Icon={() => <EditIcon fill={colors.suvaGrey} />}
                        className="button-icon"
                        onClick={() => {
                          // dispatch(selectCurrentClient(undefined));
                          onEdit(notification);
                        }}
                      />
                      {isCustomer && (
                        <>
                          <ButtonIcon
                            id={`trash-icon-notification-${index}`}
                            title={t('general_delete')}
                            type="button"
                            onClick={() => onRemove(notification)}
                            Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                            className="button-icon mr-n3 ml-2"
                            isDisabled={notification.answers?.length > 0}
                          />
                        </>
                      )}
                    </div>
                  </TdActions>
                </TBodyTr>
              ))}
            </Tbody>
          </Table>
          {!notificationData.length && (
            <div className="d-flex flex-column justify-content-center align-items-center">
              <NotificationIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
              <span>{t('notification_not_found')}</span>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={showConfirmRemoveNotifica}
        toggle={onCancelRemove}
        confirmMessage={t('custom_feild_delete_field_confirmation').replace(
          '{fieldName}',
          SelectedRemoveNotifi?.name
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={t('custom_field_delete_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('custom_feild_delete_field_success').replace(
          '{fieldName}',
          SelectedRemoveNotifi?.name
        )}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
    </>
  );
};

const TdActions = styled.td`
  text-align: end;
  width: 10% !important;
`;

const TdDate = styled.td`
  width: 19%;
`;

const Tbody = styled.tbody`
  font-size: ${fontSizes.sm}px;
  .link-name-notification {
    display: flex;
    width: 100%;
    P {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      width: 100px;
      flex: 1 1 auto;
      @media ${device.sm} {
        width: 100px;
      }
    }
    cursor: pointer;
    &:hover {
      color: ${colors.suvaGrey};
    }
  }
`;
const TBodyTr = styled.tr`
  font-family: ${({ numAnswersUnread }) => (numAnswersUnread > 0 ? fonts.bold : fonts.default)};
`;

NotificationsTable.defaultProps = {
  notificationData: [],
};

NotificationsTable.propTypes = {
  notificationData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      dateBegin: PropTypes.string,
    })
  ),
  refreshNotificationsData: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default NotificationsTable;
